import type { HomePage } from '../core/types/pages'
import type { Tone } from '../ui/sections'

/**
 * De homepage als één verhaal (docs/VORMGEVING.md §3): Kijken → Kiezen → Opbouwen, en tot slot
 * Kom langs. Eén bron voor de volgorde, de toon en de labels: de site rendert de blokken zo,
 * het beheer (Paginateksten › Home) toont ze zo, met hun rol en een link naar precies dat blok.
 *
 * `anchor` = het ankertje op de homepage (`/#zo-werk-ik`). `labels` = hoe een veld op díe plek
 * heet, als het algemene label (admin/adminLabels.ts) niet genoeg zegt. De volgorde ligt vast;
 * alleen de woorden en beelden zijn beheerbaar.
 */
export type HomeSection = {
  key: keyof HomePage
  anchor: string
  title: string
  role: string
  tone: Tone
  labels?: Record<string, { label: string; hint?: string }>
}

export const homeSections: readonly HomeSection[] = [
  {
    key: 'hero',
    anchor: 'opening',
    title: 'Opening',
    role: 'Wie, wat, en de hoofdknop naar Kiezen; de huidtest als tweede',
    tone: 'sand',
    labels: { cta: { label: 'Tweede knop (huidtest)', hint: 'De hoofdknop “Kies op je huidvraag” ligt vast. Leeg = alleen de hoofdknop.' } },
  },
  { key: 'manifest', anchor: 'waar-ik-voor-sta', title: 'Waar ik voor sta', role: 'De balans: salon én thuis', tone: 'blush' },
  {
    key: 'story',
    anchor: 'zo-werk-ik',
    title: 'Zo werk ik',
    role: 'Kijken, Kiezen, Opbouwen, en door naar een afspraak',
    tone: 'sage',
    labels: { cta: { label: 'Knop (afspraak plannen)' } },
  },
  {
    key: 'concerns',
    anchor: 'kiezen',
    title: 'Wat past bij je huid',
    role: 'Kiezen: per klacht het protocol voor thuis en de behandeling',
    tone: 'shell',
    labels: { addCta: { label: 'Koopknop van het protocol', hint: 'Het bedrag staat er vanzelf naast.' } },
  },
  { key: 'protocols', anchor: 'opbouwen', title: 'Opbouwen', role: 'Wat er na je bestelling gebeurt: huidplan, bijsturen, opnieuw kijken', tone: 'sand' },
  { key: 'reviews', anchor: 'reviews', title: 'Reviews', role: 'Bewijs, de brug naar klein beginnen', tone: 'shell' },
  { key: 'bestsellers', anchor: 'een-product', title: 'Eén product', role: 'Klein beginnen: de plank met favorieten, en de cadeaus bij elke bestelling', tone: 'sand' },
  { key: 'journal', anchor: 'journal', title: 'Journal', role: 'Veelgestelde vragen, de genoemde producten, en de app-kaart', tone: 'clay' },
  {
    key: 'visit',
    anchor: 'kom-langs',
    title: 'Kom langs',
    role: 'Afsluiter: plan een afspraak',
    tone: 'blush',
    labels: { cta: { label: 'Knop (afspraak plannen)' }, secondaryCta: { label: 'Link naar de route' } },
  },
]

/** Het ankertje van een blok. */
export const homeAnchor = (key: keyof HomePage): string => homeSections.find((s) => s.key === key)?.anchor ?? ''
